import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import React, { FC, useMemo, useState } from "react";
import { useListContext } from "react-admin";

export interface ColumnDef {
  key: string;
  label: string;
  render: (record: any) => React.ReactNode;
}

export interface GroupConfig {
  getGroupKey: (record: any) => string;
  getGroupLabel: (key: string) => string;
  getGroupColor?: (key: string) => string;
}

interface GroupedDatagridProps {
  columns: ColumnDef[];
  group?: GroupConfig;
  rowClick?: (id: any, basePath: string, record: any) => string;
}

const GroupedDatagrid: FC<GroupedDatagridProps> = ({
  columns,
  group,
  rowClick,
}) => {
  const { ids, data, basePath } = useListContext();
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    new Set(),
  );

  const records = useMemo(
    () => ids.map((id: any) => data[id]).filter(Boolean),
    [ids, data],
  );

  const groups = useMemo(() => {
    if (!group || records.length === 0) {
      return [{ key: "__all__", label: "", color: undefined, records }];
    }

    const map = new Map<string, any[]>();
    records.forEach((record) => {
      const key = group.getGroupKey(record);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(record);
    });

    return Array.from(map.entries())
      .map(([key, recs]) => ({
        key,
        label: group.getGroupLabel(key),
        color: group.getGroupColor?.(key),
        records: recs,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [records, group]);

  const toggleGroup = (key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (records.length === 0) return null;

  // const colSpan = columns.length + (group ? 1 : 0);
  const colSpan = columns.length;

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          {/* {group && (
            <TableCell
              padding="none"
              style={{
                width: 0,
                minWidth: 0,
                maxWidth: 0,
              }}
            />
          )} */}
          {columns.map((col) => (
            <TableCell
              key={col.key}
              style={{ fontWeight: 600, whiteSpace: "nowrap" }}
            >
              {col.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {groups.map((grp) => {
          const isCollapsed = collapsedGroups.has(grp.key);

          return (
            <React.Fragment key={grp.key}>
              {group && (
                <TableRow
                  hover
                  onClick={() => toggleGroup(grp.key)}
                  style={{
                    cursor: "pointer",
                    backgroundColor: grp.color ? `${grp.color}22` : "#f5f5f5",
                  }}
                >
                  <TableCell colSpan={colSpan} style={{ padding: "6px 8px" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      <IconButton size="small" disableRipple>
                        {isCollapsed ? (
                          <ExpandMoreIcon fontSize="small" />
                        ) : (
                          <ExpandLessIcon fontSize="small" />
                        )}
                      </IconButton>
                      <Typography
                        variant="subtitle2"
                        style={{ fontWeight: 600, fontSize: "0.8rem" }}
                      >
                        {grp.label}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        style={{ marginLeft: 4 }}
                      >
                        ({grp.records.length})
                      </Typography>
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {!isCollapsed &&
                grp.records.map((record) => (
                  <TableRow
                    key={record.id}
                    hover
                    style={{ cursor: rowClick ? "pointer" : "default" }}
                    onClick={
                      rowClick
                        ? () => rowClick(record.id, basePath, record)
                        : undefined
                    }
                  >
                    {/* {group && (
                      <TableCell
                        padding="none"
                        style={{
                          width: 0,
                          minWidth: 0,
                          maxWidth: 0,
                        }}
                      />
                    )} */}
                    {columns.map((col) => (
                      <TableCell key={col.key}>{col.render(record)}</TableCell>
                    ))}
                  </TableRow>
                ))}
            </React.Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
};

export default GroupedDatagrid;
